import type { GoalProgress as GoalProgressData, GoalWeekRow } from '../api';
import { KpiCard, computeTrend } from '../components/KpiCard';
import { money, moneyCompact, toPlotValue } from '../format';

function signedMoney(value: string): string {
  if (value.startsWith('-')) return money(value);
  if (/^0+(\.0+)?$/.test(value)) return money(value);
  return `+${money(value)}`;
}

function signedCompact(value: string): string {
  if (value.startsWith('-')) return moneyCompact(value);
  if (/^0+(\.0+)?$/.test(value)) return moneyCompact(value);
  return `+${moneyCompact(value)}`;
}

function weeksThrough(progress: GoalProgressData): GoalWeekRow[] {
  return progress.weeks.filter((w) => w.weekNumber <= progress.asOfWeekNumber);
}

function plotOrZero(value: string | null): number {
  return value === null ? 0 : toPlotValue(value);
}

function PercentBadge({
  pct,
  label,
  tone,
}: {
  pct: number;
  label: string;
  tone: 'profit' | 'caution';
}) {
  const classes = tone === 'profit' ? 'bg-profit/10 text-profit' : 'bg-caution/10 text-caution';
  return (
    <div className={`flex flex-col items-center rounded-lg px-2.5 py-1.5 ${classes}`}>
      <span className="text-sm font-bold">{`${pct.toFixed(0)}%`}</span>
      <span className="text-[9px] font-medium uppercase tracking-wide opacity-70">{label}</span>
    </div>
  );
}

function WeeksOnTarget({ hit, scored }: { hit: number; scored: number }) {
  if (scored === 0) return null;
  const pct = (hit / scored) * 100;
  return (
    <div className="w-full">
      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-overlay">
        <div
          className="h-full rounded-full bg-profit"
          style={{ width: `${Math.max(0, Math.min(100, pct)).toFixed(1)}%` }}
        />
      </div>
      <p className="mt-0.5 text-[9px] text-ink-faint">
        {String(hit)} of {String(scored)} weeks on target
      </p>
    </div>
  );
}

function FillBar({
  pct,
  left,
  right,
  tone,
}: {
  pct: number;
  left: string;
  right: string;
  tone: 'profit' | 'caution';
}) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="w-full">
      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-overlay">
        <div
          className={`h-full rounded-full ${tone === 'profit' ? 'bg-profit' : 'bg-caution'}`}
          style={{ width: `${clamped.toFixed(1)}%` }}
        />
      </div>
      <div className="mt-0.5 flex justify-between text-[9px] text-ink-faint">
        <span>{left}</span>
        <span>{right}</span>
      </div>
    </div>
  );
}

export function GoalProgress({ progress, title }: { progress: GoalProgressData; title?: string }) {
  const elapsed = weeksThrough(progress);
  const prior = elapsed.length >= 2 ? elapsed[elapsed.length - 2] : undefined;

  const actualSpark = elapsed.map((w) => plotOrZero(w.actual));
  const avgSpark = elapsed.map((w) => plotOrZero(w.ytdAverage));
  const remainingSpark = elapsed.map((w) => toPlotValue(w.goalRemaining));
  const needSpark = elapsed.map((w) => plotOrZero(w.needToAverage));

  let runningTarget = 0;
  const ptpSpark = elapsed.map((w) => {
    runningTarget += toPlotValue(w.weeklyTarget);
    return toPlotValue(w.ytd) - runningTarget;
  });

  const annual = toPlotValue(progress.annualAmount);
  const ytd = toPlotValue(progress.ytd);
  const ytdGoal = toPlotValue(progress.ytdGoal);
  const pctOfAnnual = annual > 0 ? (ytd / annual) * 100 : 0;
  const pctOfYtdTarget = ytdGoal > 0 ? (ytd / ytdGoal) * 100 : 0;
  const yearElapsedPct =
    progress.weeks.length > 0 ? (progress.asOfWeekNumber / progress.weeks.length) * 100 : 0;

  const weeksHit = elapsed.filter(
    (w) => w.actual !== null && w.variance !== null && !w.variance.startsWith('-'),
  ).length;
  const weeksMiss = elapsed.filter(
    (w) => w.actual !== null && w.variance !== null && w.variance.startsWith('-'),
  ).length;

  const ptpBehind = progress.pointToPoint.startsWith('-');
  const weekBehind = progress.weeklyVariance !== null && progress.weeklyVariance.startsWith('-');

  const weeklyTrend =
    progress.weeklyActual !== null && prior?.actual !== null && prior !== undefined
      ? computeTrend(toPlotValue(progress.weeklyActual), toPlotValue(prior.actual))
      : undefined;

  const avgVsNeed =
    progress.ytdAverage !== null && progress.needToAverage !== null
      ? toPlotValue(progress.needToAverage) / Math.max(toPlotValue(progress.ytdAverage), 1)
      : null;

  return (
    <div>
      {title !== undefined && (
        <p className="mb-3 text-sm font-semibold text-ink">
          {title} · {money(progress.annualAmount)} annual goal
          <span className="ml-2 font-normal text-ink-faint">
            Week {String(progress.asOfWeekNumber)} of {String(progress.weeks.length)} ·{' '}
            {pctOfAnnual.toFixed(0)}% earned
          </span>
        </p>
      )}
      <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        <KpiCard
          label="YTD GP"
          value={moneyCompact(progress.ytd)}
          exact={money(progress.ytd)}
          subtitle={`${pctOfAnnual.toFixed(0)}% of ${moneyCompact(progress.annualAmount)} · year ${yearElapsedPct.toFixed(0)}% elapsed`}
          tone="profit"
          delay={0}
          sparkline={actualSpark}
          sparklineColor="#16a34a"
          sparklineLabel="actual/wk"
          footer={
            <FillBar
              pct={pctOfAnnual}
              left={`${moneyCompact(progress.ytd)} earned`}
              right={`${moneyCompact(progress.goalRemaining)} to go`}
              tone="profit"
            />
          }
        >
          <PercentBadge pct={pctOfAnnual} label="of goal" tone="profit" />
        </KpiCard>

        <KpiCard
          label="Point to point"
          value={signedCompact(progress.pointToPoint)}
          exact={signedMoney(progress.pointToPoint)}
          subtitle={`${ptpBehind ? 'Behind' : 'Ahead of'} ${moneyCompact(progress.ytdGoal)} planned through week ${String(progress.asOfWeekNumber)}`}
          tone={ptpBehind ? 'caution' : 'profit'}
          delay={40}
          sparkline={ptpSpark}
          sparklineColor={ptpBehind ? '#d97706' : '#16a34a'}
          sparklineLabel="gap"
          footer={<WeeksOnTarget hit={weeksHit} scored={weeksHit + weeksMiss} />}
        >
          <PercentBadge
            pct={pctOfYtdTarget}
            label="of pace"
            tone={pctOfYtdTarget >= 100 ? 'profit' : 'caution'}
          />
        </KpiCard>

        <KpiCard
          label="Weekly actual"
          value={progress.weeklyActual === null ? '—' : moneyCompact(progress.weeklyActual)}
          exact={progress.weeklyActual === null ? undefined : money(progress.weeklyActual)}
          subtitle={`Target ${moneyCompact(progress.weeklyTarget)}${
            progress.weeklyVariance !== null ? ` · ${signedCompact(progress.weeklyVariance)}` : ''
          }`}
          tone={weekBehind ? 'caution' : 'profit'}
          delay={80}
          trend={weeklyTrend}
          trendContext="vs last week"
          sparkline={actualSpark}
          sparklineColor={weekBehind ? '#d97706' : '#16a34a'}
          sparklineLabel="actual/wk"
        />

        <KpiCard
          label="Weekly average"
          value={progress.ytdAverage === null ? '—' : moneyCompact(progress.ytdAverage)}
          exact={progress.ytdAverage === null ? undefined : money(progress.ytdAverage)}
          subtitle={`${String(elapsed.filter((w) => w.actual !== null).length)} weeks with actuals`}
          tone="profit"
          delay={120}
          sparkline={avgSpark}
          sparklineColor="#16a34a"
          sparklineLabel="avg YTD"
        />

        <KpiCard
          label="Goal remaining"
          value={moneyCompact(progress.goalRemaining)}
          exact={money(progress.goalRemaining)}
          subtitle={`${String(progress.weeksRemaining)} weeks left to earn ${moneyCompact(progress.goalRemaining)}`}
          delay={160}
          sparkline={remainingSpark}
          sparklineColor="#1c1c1a"
          sparklineLabel="remaining"
          footer={
            <FillBar
              pct={yearElapsedPct}
              left={`Week ${String(progress.asOfWeekNumber)}`}
              right={`${String(progress.weeksRemaining)} left`}
              tone="profit"
            />
          }
        />

        <KpiCard
          label="Need to average"
          value={progress.needToAverage === null ? '—' : moneyCompact(progress.needToAverage)}
          exact={progress.needToAverage === null ? undefined : money(progress.needToAverage)}
          subtitle={
            avgVsNeed === null
              ? `${String(progress.weeksRemaining)} weeks to close the year`
              : `${avgVsNeed.toFixed(1)}× current weekly average`
          }
          tone={
            avgVsNeed !== null && avgVsNeed > 1.25
              ? 'caution'
              : avgVsNeed !== null && avgVsNeed <= 1
                ? 'profit'
                : 'neutral'
          }
          delay={200}
          sparkline={needSpark}
          sparklineColor="#d97706"
          sparklineLabel="need/wk"
        />
      </div>
    </div>
  );
}
